// src/routes/marketplace/marketplace-core-routes.tsx
import { lazy } from 'react';
import type { RoutesProps } from '../routes';
import {
    FaStore,
    FaChartBar,
    FaUpload,
    FaFileContract,
    FaSearch,
} from 'react-icons/fa';
import { BiPurchaseTag } from 'react-icons/bi';
import { HiDocumentSearch } from 'react-icons/hi';

// Core marketplace components
const MarketplaceHome = lazy(() => import('@/pages/marketplace/MarketplaceHome'));
const MarketplaceComingSoon = lazy(() => import('@/pages/management/MarketplaceComingSoon/MarketplaceComingSoon'));
const MarketplaceDashboard = lazy(() => import('@/pages/marketplace/MarketplaceDashboard'));
const ContentPublishing = lazy(() => import('@/pages/marketplace/components/ContentPublishing'));
const MarketplaceUnified = lazy(() => import('@/pages/marketplace/MarketplacePageUnified'));
const PurchasePage = lazy(() => import('@/pages/marketplace/PurchasePage'));
const CheckoutPage = lazy(() => import('@/pages/marketplace/CheckoutPage'));

const ContentDetailPage = lazy(() => import('@/pages/marketplace/components/ContentDetailPage'));
const LicenseManagement = lazy(() => import('@/pages/marketplace/LicenseManagement'));
const LicenseRequestPage = lazy(() => import('@/pages/marketplace/LicenseRequestPage'));
// const MarketplaceLicensesPage = lazy(() => import('@/pages/marketplace/MarketplaceLicensesPage'));

//const MyLicenseRequests = lazy(() => import('@/pages/marketplace/MyLicenseRequests'));

// Core marketplace routes
export const marketplaceCoreRoutes: RoutesProps[] = [
    // Public marketplace
    {
        path: '/marketplace',
        name: 'Vietrro Marketplace',
        // element: <MarketplaceHome />,
        element: <MarketplaceComingSoon />,
        roles: ['guest', 'right_owner', 'licensee', 'admin'],
        icon: FaStore,
    },
    {
        path: '/marketplace/browse',
        name: 'Duyệt nội dung',
        element: <MarketplaceUnified />,
        roles: ['guest', 'right_owner', 'licensee', 'admin'],
        icon: FaSearch,
    },
    {
        path: '/marketplace/content/:id',
        name: 'Chi tiết nội dung',
        element: <ContentDetailPage />,
        roles: ['guest', 'right_owner', 'licensee', 'admin'],
    },
    {
        path: '/marketplace/content/:contentId/purchase',
        name: 'Chi tiết nội dung',
        element: <PurchasePage />,
        roles: ['guest', 'right_owner', 'licensee', 'admin'],
    },
    {
        path:"/marketplace/checkout/:contentId/:licenseId" ,
        name: 'Thanh toasn',
        element: <CheckoutPage />,
        roles: ['guest', 'right_owner', 'licensee', 'admin'],
    },
    {
        path: '/marketplace/dashboard',
        name: 'Tổng quan Marketplace',
        element: <MarketplaceDashboard />,
        roles: ['right_owner', 'admin', 'staff'],
        icon: FaChartBar,
    },
    {
        path: '/marketplace/publishing',
        name: 'Xuất bản tác phẩm',
        element: <ContentPublishing />,
        roles: ['right_owner'],
        icon: FaUpload,
    },

    // License management
    {
        path: '/marketplace/licenses/manage',
        name: 'Quản lý giấy phép',
        element: <LicenseManagement />,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal'],
        icon: FaFileContract,
    },
    // ✅ Flow A: Direct license request for specific content
    {
        path: '/marketplace/license-request/:contentId',
        element: <LicenseRequestPage />,
        name: 'License Request',
        roles: ['authenticated','right_owner']
    },
    // {
    //     path: '/marketplace/license-request/:contentId/:licenseId',
    //     element: <LicenseRequestPage />,
    //     name: 'License Request with License',
    //     roles: ['authenticated','right_owner']
    // },

    // ✅ Flow B: Browse contents and request license
    // {
    //     path: '/marketplace/licenses',
    //     element: <MarketplaceLicensesPage />,
    //     name: 'Buy Licenses',
    //     roles: ['authenticated','right_owner']
    // },
    // {
    //     path: '/marketplace/license/my-requests',
    //     name: 'Yêu cầu của tôi',
    //     element: <MyLicenseRequests />,
    //     roles: ['licensee', 'right_owner'],
    //     icon: HiDocumentSearch,
    // },
];

// Navigation structure for core marketplace
export const marketplaceCoreNavigationStructure = {
    home: {
        key: 'marketplace-home',
        translationKey: 'marketplace.home',
        path: '/marketplace',
        icon: FaStore,
        roles: ['guest', 'right_owner', 'licensee', 'admin'],
    },
    browse: {
        key: 'marketplace-browse',
        translationKey: 'marketplace.browse',
        path: '/marketplace/browse',
        icon: FaSearch,
        roles: ['guest', 'right_owner', 'licensee', 'admin'],
    },
    dashboard: {
        key: 'marketplace-dashboard',
        translationKey: 'marketplace.dashboard',
        path: '/marketplace/dashboard',
        icon: FaChartBar,
        roles: ['right_owner', 'admin', 'staff'],
    },
    publishing: {
        key: 'content-publishing',
        translationKey: 'marketplace.publishing',
        path: '/marketplace/publishing',
        icon: FaUpload,
        roles: ['right_owner'],
    },
    licenses: {
        key: 'license-management',
        translationKey: 'marketplace.licenses',
        path: '/marketplace/licenses',
        icon: FaFileContract,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal'],
    },
    licenseRequest: {
        key: 'license-request',
        translationKey: 'marketplace.licenseRequest',
        path: '/marketplace/license/request',
        icon: BiPurchaseTag,
        roles: ['licensee', 'right_owner'],
    },
    myRequests: {
        key: 'my-license-requests',
        translationKey: 'marketplace.myRequests',
        path: '/marketplace/license/my-requests',
        icon: HiDocumentSearch,
        roles: ['licensee', 'right_owner'],
    },
};

// Helper functions
export const hasAccessToMarketplaceCore = (userRoles: string[]): boolean => {
    const allowedRoles = ['right_owner', 'licensee', 'admin', 'staff', 'guest'];
    return userRoles.some(role => allowedRoles.includes(role));
};

export const canPublishContent = (userRoles: string[]): boolean => {
    return userRoles.includes('right_owner');
};

export const canBuyLicenses = (userRoles: string[]): boolean => {
    return userRoles.includes('licensee') || userRoles.includes('right_owner');
};

export default marketplaceCoreRoutes;
